import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  Stack,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
  InputBase,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LogoSection from '../common/LogoSection';

// Social media links configuration
const SOCIAL_LINKS = [
  { icon: FacebookIcon, url: 'https://facebook.com', label: 'Facebook', color: '#1877F2' },
  { icon: TwitterIcon, url: 'https://twitter.com', label: 'Twitter', color: '#1DA1F2' },
  { icon: InstagramIcon, url: 'https://instagram.com', label: 'Instagram', color: '#E4405F' },
  { icon: LinkedInIcon, url: 'https://linkedin.com', label: 'LinkedIn', color: '#0A66C2' },
];

// Navigation links configuration
const QUICK_LINKS = [
  { to: '/about', label: 'About Us' },
  { to: '/tours', label: 'Tours & Packages' },
  { to: '/services', label: 'Our Services' },
  { to: '/contact', label: 'Contact Us' },
  { to: '/corporate-booking', label: 'Corporate Booking' },
];

const DESTINATION_LINKS = [
  { to: '/tours?search=Paris', label: 'Paris Tours' },
  { to: '/tours?search=Bali', label: 'Bali Packages' },
  { to: '/tours?search=Dubai', label: 'Dubai Holidays' },
  { to: '/tours?search=Maldives', label: 'Maldives Getaways' },
  { to: '/tours?search=Switzerland', label: 'Switzerland Tours' },
];

const LEGAL_LINKS = [
  { to: '/privacy-policy', label: 'Privacy Policy' },
  { to: '/terms-conditions', label: 'Terms & Conditions' },
];

const LOGO_TEXT = process.env.REACT_APP_LOGO_TEXT || 'Travel Agency';

const CONTACT_INFO = [
  { icon: EmailIcon, text: process.env.REACT_APP_EMAILID || 'info@travelagency.com', href: `mailto:${process.env.REACT_APP_EMAILID || 'info@travelagency.com'}` },
  { icon: PhoneIcon, text: process.env.REACT_APP_PHONE_NUMBER || '+1 (555) 123-4567', href: `tel:${process.env.REACT_APP_PHONE_NUMBER || '+15551234567'}` },
  { icon: LocationOnIcon, text: 'Your Trusted Travel Partner' },
];

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [email, setEmail] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (email.trim()) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subscribers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (!response.ok) {
          console.error('Newsletter subscription failed');
        }
      } catch (err) {
        console.error('Newsletter subscription failed:', err);
      }
      setEmail('');
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#0B1120',
        color: 'white',
        pt: { xs: 7, md: 9 },
        pb: { xs: 3, md: 4 },
        position: 'relative',
        overflow: 'hidden',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, #2563eb 0%, #0891b2 100%)',
        },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
        <Grid container spacing={{ xs: 5, md: 6, lg: 8 }} justifyContent="space-between">
          {/* Company Info & Newsletter */}
          <Grid item xs={12} md={4} lg={3.5}>
            <Box sx={{ mb: 3 }}>
              <LogoSection
                size="small"
                textSx={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.5px' }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.7,
                mb: 4,
                lineHeight: 1.8,
                fontSize: '0.9rem',
              }}
            >
              Your trusted partner for memorable travel experiences. We offer the best tour packages
              for domestic and international destinations, ensuring every journey is extraordinary.
            </Typography>

            {/* Newsletter Signup */}
            <Box
              component="form"
              onSubmit={handleNewsletterSubmit}
              sx={{ mb: 4 }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  display: 'block',
                  mb: 1.5,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: 'rgba(255,255,255,0.9)',
                }}
              >
                Subscribe to our newsletter
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  bgcolor: 'rgba(255,255,255,0.04)',
                  borderRadius: 1.5,
                  border: '1px solid rgba(255,255,255,0.1)',
                  overflow: 'hidden',
                  transition: 'border-color 0.2s',
                  '&:focus-within': {
                    borderColor: '#38bdf8',
                  },
                }}
              >
                <InputBase
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{
                    ml: 2,
                    flex: 1,
                    color: 'white',
                    fontSize: '0.875rem',
                    py: 1,
                    '& input::placeholder': {
                      color: 'rgba(255,255,255,0.4)',
                      opacity: 1,
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  sx={{
                    borderRadius: { xs: 0, sm: 0 },
                    px: 3,
                    py: 1.25,
                    bgcolor: '#0C2340',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    width: { xs: '100%', sm: 'auto' },
                    mt: { xs: 1.5, sm: 0 },
                    '&:hover': {
                      bgcolor: '#E2C56A',
                    },
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            </Box>

            {/* Social Media */}
            <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
              {SOCIAL_LINKS.map((social, index) => (
                <IconButton
                  key={index}
                  component="a"
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.2s ease-in-out',
                    width: 38,
                    height: 38,
                    '&:hover': {
                      color: '#ffffff',
                      bgcolor: social.color,
                      borderColor: social.color,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(social.color, 0.3)}`,
                    },
                  }}
                >
                  <social.icon sx={{ fontSize: 18 }} />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} md={2}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 3,
                color: 'white',
                fontSize: '0.95rem',
              }}
            >
              Quick Links
            </Typography>
            <Stack spacing={2}>
              {QUICK_LINKS.map((link) => (
                <Link
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  color="inherit"
                  sx={{
                    opacity: 0.7,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    display: 'inline-block',
                    fontSize: '0.875rem',
                    '&:hover': {
                      opacity: 1,
                      color: '#38bdf8',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Destinations */}
          <Grid item xs={6} md={2}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 3,
                color: 'white',
                fontSize: '0.95rem',
              }}
            >
              Destinations
            </Typography>
            <Stack spacing={2}>
              {DESTINATION_LINKS.map((link) => (
                <Link
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  color="inherit"
                  sx={{
                    opacity: 0.7,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    display: 'inline-block',
                    fontSize: '0.875rem',
                    '&:hover': {
                      opacity: 1,
                      color: '#38bdf8',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Contact & Legal */}
          <Grid item xs={12} md={4} lg={3}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 3,
                color: 'white',
                fontSize: '0.95rem',
              }}
            >
              Contact Us
            </Typography>
            <Stack spacing={2.5} sx={{ mb: 4 }}>
              {CONTACT_INFO.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <item.icon sx={{ fontSize: 18, color: '#38bdf8' }} />
                  </Box>
                  <Box sx={{ mt: 0.5 }}>
                    {item.href ? (
                      <Link
                        href={item.href}
                        color="inherit"
                        sx={{
                          opacity: 0.8,
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          transition: 'opacity 0.2s ease, color 0.2s',
                          '&:hover': { opacity: 1, color: '#38bdf8' },
                        }}
                      >
                        {item.text}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.875rem' }}>
                        {item.text}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>

            {/* Legal Links */}
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              {LEGAL_LINKS.map((link) => (
                <Link
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  color="inherit"
                  sx={{
                    opacity: 0.5,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8rem',
                    '&:hover': {
                      opacity: 0.9,
                      color: 'white',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 4, md: 5 }, borderColor: 'rgba(255,255,255,0.08)' }} />

        {/* Copyright & Developer Credit */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.6, fontSize: '0.875rem' }}>
            © {new Date().getFullYear()} {LOGO_TEXT}. All rights reserved.
          </Typography>

          <Typography variant="body2" sx={{ opacity: 0.6, fontSize: '0.875rem' }}>
            Designed & Developed by{' '}
            <Link
              href="https://webstrivetechnology.in/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: '#38bdf8',
                textDecoration: 'none',
                fontWeight: 600,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.8 }
              }}
            >
              Webstrive Technology
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;